/**
 * Sending the list somewhere (#1293).
 *
 * Tier zero, and it covers most of the value for none of the cost: it works
 * with every retailer, every delivery service and every spouse, and it never
 * breaks because somebody changed an API.
 *
 * ⚠️ The text is fetched when the list opens, not when a button is tapped.
 * navigator.share must be called inside the gesture that asked for it; an
 * await in that handler loses the transient activation on Safari and the
 * share sheet silently never appears.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { twoPlates } from "../api/twoPlates";
import { SendToShop } from "./SendToShop";

export function ShareList({ planId }: { planId: number }) {
  const { data } = useQuery({
    queryKey: ["export", planId],
    queryFn: () => twoPlates.exportList(planId),
    staleTime: 0,
  });
  const [said, setSaid] = useState<string | null>(null);

  const text = data?.text ?? "";
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  function say(msg: string) {
    setSaid(msg);
    setTimeout(() => setSaid(null), 2500);
  }

  return (
    <div className="border-line rounded-xl border p-4 print:hidden">
      <p className="text-muted text-xs uppercase tracking-wide">Send it somewhere</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {canShare && (
          <button
            onClick={() => {
              // No await before this call.
              navigator.share({ title: "Shopping list", text }).catch(() => {});
            }}
            disabled={!text}
            className="border-line rounded-lg border px-4 py-2.5 text-sm font-medium disabled:opacity-40"
          >
            Share
          </button>
        )}
        <button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              say("Copied. Paste it anywhere.");
            } catch {
              say("Could not copy — long-press the list to select it instead.");
            }
          }}
          disabled={!text}
          className="border-line rounded-lg border px-4 py-2.5 text-sm font-medium disabled:opacity-40"
        >
          Copy
        </button>
        <a
          href={text ? `mailto:?subject=${encodeURIComponent("Shopping list")}&body=${encodeURIComponent(text)}` : "#"}
          className="border-line rounded-lg border px-4 py-2.5 text-sm font-medium"
        >
          Email
        </a>
        <button
          onClick={() => window.print()}
          className="border-line rounded-lg border px-4 py-2.5 text-sm font-medium"
        >
          Print
        </button>
      </div>

      {said && <p className="text-plate-b mt-3 text-sm">{said}</p>}

      <p className="text-muted mt-3 text-xs">
        Plain text, grouped by aisle. It pastes into a notes app, a text message
        or a delivery order without turning into anything else.
      </p>

      <div className="border-line mt-4 border-t pt-3">
        <SendToShop planId={planId} />
      </div>
    </div>
  );
}
